import {
  DropdownBasic,
  EmailInput,
  TelInput,
  TextInput,
} from '@designsforhealth/dfh-react-components'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { firstValueFrom } from 'rxjs'
import { useReCaptcha } from '../../lib/auth/hooks/useReCaptcha'
import { langCodes } from '../../lib/config/lang'
import { DocumentType, SpecialtyAction } from '../../lib/form'
import {
  COMPANY_NAME_MAX_LENGTH,
  companyNameMaxLengthRule,
  EMAIL_MAX_LENGTH,
  emailMaxLengthRule,
  FIRST_NAME_MAX_LENGTH,
  firstNameMaxLengthRule,
  LAST_NAME_MAX_LENGTH,
  lastNameMaxLengthRule,
  PHONE_MAX_LENGTH,
  PHONE_MIN_LENGTH,
  phoneMaxLengthRule,
  phoneMinLengthRule,
} from '../../lib/form/fieldLengthRules'
import { logAndCaptureException } from '../../lib/monitoring'
import {
  RegistrationAddressFieldValues,
  RegistrationData,
  RegistrationFieldValues,
  RegistrationPractitionerSelectOptions,
  RegistrationPractitionerUserMetadata,
  SubmitDataValidationHandler,
  useRegistrationForm,
  useSetLoginFactory,
  validatePasswordsMatch,
  validateTerms,
} from '../../lib/registration'
import { useLoginAfterRegistration } from '../../lib/registration/request/login-after'
import {
  practitionerRegistrationRequest$,
  setPractitionerRegistrationInput,
  useLatestPractitionerRegistrationInput,
} from '../../lib/registration/request/practitioner'
import {
  formDisabledStates,
  formProcessingStates,
  useRegistrationState,
} from '../../lib/registration/state'
import { useSiteMetadata } from '../../lib/sanity/hooks/useSiteMetadata'
import { useSiteSettings } from '../../lib/sanity/hooks/useSiteSettings'
import { FileUpload } from '../upload/FileUpload'
import { LoginAfterRegistrationErrorMessage } from './LoginAfterRegistrationErrorMessage'
import { RegistrationFormAddress } from './RegistrationFormAddress'
import { RegistrationFormHelpButton } from './RegistrationFormHelpButton'
import { RegistrationFormPasswordInputs } from './RegistrationFormPasswordInputs'
import { RegistrationFormReCaptcha } from './RegistrationFormReCaptcha'
import { RegistrationFormSimpleTerms } from './RegistrationFormSimpleTerms'
import { RegistrationFormSubmit } from './RegistrationFormSubmit'
import { RegistrationMessage } from './RegistrationMessage'
import { RegistrationUserActionRequired } from './RegistrationUserActionRequired'
import * as Styled from './styled'

export type RegistrationFormPractitionerProps = {
  setSubmitting?: (submitting: boolean) => void
}

type RegistrationPractitionerFieldValues = RegistrationFieldValues &
  RegistrationAddressFieldValues & {
    companyName: string
    phone: string
    licenseNumber: string
    certificateUrl?: string
    specialty: {
      label: string
      value: string
      practitionerAction?: SpecialtyAction
      practitionerDocument?: DocumentType
    }
    primaryBrand?: { label: string; value: string }
    practiceType?: { label: string; value: string }
  }

const parseFieldValuesToRequestData = ({
  firstName,
  lastName,
  email,
  password,
  companyName,
  phone,
  licenseNumber,
  certificateUrl,
  specialty,
  practiceType,
  primaryBrand,
  street1,
  street2,
  city,
  state,
  zipCode,
  country,
}: RegistrationPractitionerFieldValues): RegistrationData &
  RegistrationPractitionerUserMetadata => {
  return {
    firstName,
    lastName,
    email,
    password,
    type: 'practitioner',
    company_name: companyName,
    phone,
    license_number: licenseNumber,
    certificate_url: certificateUrl,
    specialty: JSON.stringify(specialty),
    practice_type: practiceType?.value,
    primary_brand: primaryBrand?.value,
    address: JSON.stringify({
      street1,
      street2,
      city,
      state,
      zip_code: zipCode,
      country,
    }),
  }
}

const submitDataValidationHandlers: ReadonlyArray<
  SubmitDataValidationHandler<RegistrationPractitionerFieldValues>
> = [validatePasswordsMatch, validateTerms]

export const RegistrationFormPractitioner: React.VFC<RegistrationFormPractitionerProps> = ({
  setSubmitting = () => {
    // noop
  },
}) => {
  useLatestPractitionerRegistrationInput()
  useLoginAfterRegistration()
  useSetLoginFactory()

  const [
    { messages, dismissMessage, patterns },
    {
      register,
      watch,
      setValue,
      handleSubmit,
      setError,
      errors,
      control,
      formState: { isSubmitting },
    },
  ] = useRegistrationForm<RegistrationPractitionerFieldValues>({ submitDataValidationHandlers })

  const formErrorsAlertRef = useRef<HTMLFormElement>(null)

  const currentSpecialty = watch('specialty')
  const { lang } = useSiteSettings() || {}
  const [selectOptions, setSelectOptions] = useState<RegistrationPractitionerSelectOptions>({
    brand: [],
    practiceType: [],
    specialty: [],
  })

  useEffect(() => {
    switch (lang) {
      case langCodes.en_CA:
        import('../../lib/registration/options/en-ca/practitioner')
          .then((options) => setSelectOptions(options.default))
          .catch(logAndCaptureException)
        break
      default:
        import('../../lib/registration/options/en-us/practitioner')
          .then((options) => setSelectOptions(options.default))
          .catch(logAndCaptureException)
        break
    }
  }, [lang])

  const { name: registrationState } = useRegistrationState()
  const processing = isSubmitting || formProcessingStates.includes(registrationState)
  const disabled = processing || formDisabledStates.includes(registrationState)

  const { captchaError, validateCaptcha, reCaptchaProps } = useReCaptcha()

  const scrollErrorsRefIntoView = () => {
    const formErrorsEl = formErrorsAlertRef.current
    if (formErrorsEl) {
      formErrorsEl.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const onSubmit = useCallback(
    async (fieldValues: RegistrationPractitionerFieldValues) => {
      if (currentSpecialty?.label === 'Other' && !fieldValues.certificateUrl) {
        setError('certificateUrl', {
          type: 'validate',
          message: 'You are required to upload a certificate.',
        })
        return
      }
      if (
        currentSpecialty?.practitionerAction === SpecialtyAction.LicenseAndUpload &&
        !fieldValues.certificateUrl &&
        !fieldValues.licenseNumber
      ) {
        setError('licenseNumber', {
          type: 'validate',
          message: 'You are required to provide a license number or upload a certificate.',
        })
        return
      }
      const isValidCaptcha = !!validateCaptcha()
      if (!isValidCaptcha) {
        return
      }
      setPractitionerRegistrationInput({
        input: parseFieldValuesToRequestData(fieldValues),
      })
      await firstValueFrom(practitionerRegistrationRequest$)
    },
    [validateCaptcha, currentSpecialty?.practitionerAction, setError, currentSpecialty?.label]
  )

  useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      scrollErrorsRefIntoView()
    }
  }, [errors])

  useEffect(() => {
    setSubmitting(processing)
  }, [setSubmitting, processing])

  const userActionRequiredMessage = useMemo(
    () => messages.find((message) => !!message.actions?.includes('user_action_required')),
    [messages]
  )

  const { featureFlags } = useSiteMetadata() || {}

  if (userActionRequiredMessage) {
    return <RegistrationUserActionRequired message={userActionRequiredMessage} />
  }

  return (
    <Styled.RegistrationForm noValidate onSubmit={handleSubmit(onSubmit)} ref={formErrorsAlertRef}>
      <Styled.LegalDisclaimer>
        This registration form is only for use by health care practitioners who desire to establish
        an account under their individual names. If you wish to register an account in the name of a
        business entity, please contact your DFH sales representative.
      </Styled.LegalDisclaimer>
      <Styled.Title>Account Registration</Styled.Title>
      {messages.map((message, index) => (
        <RegistrationMessage
          key={index}
          message={message}
          onDismiss={() => dismissMessage(message)}
        />
      ))}
      {registrationState === 'login_error' && <LoginAfterRegistrationErrorMessage />}
      <div>
        {errors && Object.keys(errors).length > 0 && (
          <Styled.Alert type="error">
            Please review the form for any missing or required field(s).
          </Styled.Alert>
        )}
        <Styled.FieldsRow>
          <Styled.HalfColumn>
            <TextInput
              autoComplete="given-name"
              label="Practitioner/Owner First Name"
              name="firstName"
              register={register}
              errors={errors}
              required
              disabled={disabled}
              defaultValue=""
              maxLength={FIRST_NAME_MAX_LENGTH}
              validations={firstNameMaxLengthRule}
            />
          </Styled.HalfColumn>
          <Styled.HalfColumn>
            <TextInput
              autoComplete="family-name"
              label="Practitioner/Owner Last Name"
              name="lastName"
              register={register}
              errors={errors}
              required
              disabled={disabled}
              defaultValue=""
              maxLength={LAST_NAME_MAX_LENGTH}
              validations={lastNameMaxLengthRule}
            />
          </Styled.HalfColumn>
        </Styled.FieldsRow>
        <Styled.FieldsRow>
          <TextInput
            autoComplete="organization"
            label="Practice Name"
            name="companyName"
            register={register}
            errors={errors}
            required
            disabled={disabled}
            defaultValue=""
            maxLength={COMPANY_NAME_MAX_LENGTH}
            validations={companyNameMaxLengthRule}
          />
        </Styled.FieldsRow>
        <Styled.FieldsRow>
          <EmailInput
            autoComplete="email"
            label="Practitioner Email Address"
            name="email"
            register={register}
            errors={errors}
            required
            disabled={disabled}
            defaultValue=""
            validations={{
              ...emailMaxLengthRule,
              pattern: patterns.email,
            }}
            maxLength={EMAIL_MAX_LENGTH}
          />
        </Styled.FieldsRow>
        <Styled.FieldsRow>
          <TelInput
            autoComplete="tel"
            label="Practitioner Phone Number"
            name="phone"
            register={register}
            errors={errors}
            required
            disabled={disabled}
            defaultValue=""
            maxLength={PHONE_MAX_LENGTH}
            minLength={PHONE_MIN_LENGTH}
            validations={{
              ...phoneMaxLengthRule,
              ...phoneMinLengthRule,
            }}
          />
        </Styled.FieldsRow>
        <RegistrationFormPasswordInputs
          disabled={disabled}
          defaultValue=""
          errors={errors}
          register={register}
          watch={watch}
        />
        <Styled.Title>
          <Styled.TitleHelper>Please tell us about your practice:</Styled.TitleHelper>
        </Styled.Title>
        <Styled.FieldsRow>
          <DropdownBasic
            label="Practitioner Specialty"
            name="specialty"
            control={control}
            errors={errors}
            options={selectOptions.specialty}
            required
            disabled={disabled}
            defaultValue={undefined}
          />
        </Styled.FieldsRow>
        {featureFlags?.displayPracticeType && (
          <Styled.FieldsRow>
            <DropdownBasic
              label="Practice Type"
              name="practiceType"
              control={control}
              errors={errors}
              options={selectOptions.practiceType}
              required
              disabled={disabled}
              defaultValue={undefined}
            />
          </Styled.FieldsRow>
        )}
        {currentSpecialty?.practitionerAction === SpecialtyAction.License && (
          <Styled.FieldsRow>
            <TextInput
              label="Practitioner License #"
              name="licenseNumber"
              register={register}
              errors={errors}
              required
              disabled={disabled}
              defaultValue=""
            />
            <RegistrationFormHelpButton>
              Please provide your license number so that we may verify your license status and
              qualifications.
            </RegistrationFormHelpButton>
          </Styled.FieldsRow>
        )}
        {currentSpecialty?.practitionerAction === SpecialtyAction.Upload && (
          <FileUpload
            document={currentSpecialty?.practitionerDocument}
            name="certificateUrl"
            setValue={setValue}
            register={register}
            errors={errors}
            required
          />
        )}
        {currentSpecialty?.practitionerAction === SpecialtyAction.LicenseAndUpload && (
          <>
            <Styled.FieldsRow>
              <TextInput
                label="Practitioner License #"
                name="licenseNumber"
                register={register}
                disabled={disabled}
                defaultValue=""
              />
              <RegistrationFormHelpButton>
                Please provide your license number so that we may verify your license status and
                qualifications.
              </RegistrationFormHelpButton>
            </Styled.FieldsRow>
            <FileUpload
              document={currentSpecialty?.practitionerDocument}
              name="certificateUrl"
              setValue={setValue}
              register={register}
            />
            {errors.licenseNumber && (
              <Styled.Alert type="error">{errors.licenseNumber.message}</Styled.Alert>
            )}
            {errors.certificateUrl && (
              <Styled.Alert type="error">{errors.certificateUrl.message}</Styled.Alert>
            )}
          </>
        )}
        <Styled.Title>
          <Styled.TitleHelper>Business address:</Styled.TitleHelper>
        </Styled.Title>
        <RegistrationFormAddress
          disabled={disabled}
          register={register}
          errors={errors}
          watch={watch}
          control={control}
        />
        {featureFlags?.displayPrimaryBrand && (
          <Styled.FieldsRow>
            <DropdownBasic
              label="Primary Brand Interest"
              name="primaryBrand"
              control={control}
              errors={errors}
              options={selectOptions.brand}
              required
              disabled={disabled}
              defaultValue={undefined}
            />
          </Styled.FieldsRow>
        )}
        <Styled.FieldsRow $gutter>
          <RegistrationFormSimpleTerms
            register={register}
            linkComponents={[Styled.PrivacyNoticeLink, Styled.TermsOfUseLink]}
          />
        </Styled.FieldsRow>

        <RegistrationFormReCaptcha reCaptchaProps={reCaptchaProps} captchaError={captchaError} />

        <RegistrationFormSubmit disabled={disabled} processing={processing}>
          Register
        </RegistrationFormSubmit>
      </div>
    </Styled.RegistrationForm>
  )
}
